import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Avatar, Input } from '@hrms/ui';
import { tsr } from '../../lib/api.js';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import { useAuthenticatedSession } from '../../session/SessionProvider.js';
import { ADMIN_NAV, NAV, visibleNav, type NavItem } from '../../routes/nav.js';
import { useNavSwitches } from '../../routes/useNavSwitches.js';

// The full employee profile (every tab, the transfer wizard) loads only when a result is opened —
// this search box is on every page, and the profile was most of what it cost to load.
const EmployeeDetailDialog = lazy(() =>
  import('../employees/EmployeeDetailDialog.js').then((m) => ({ default: m.EmployeeDetailDialog })),
);

interface PageHit {
  to: string;
  label: string;
  /** The section it lives under, e.g. "Attendance" or "Organisation" — blank for a top-level page. */
  section?: string;
}

/** Every page the current actor may reach, flattened once per actor/nav change — jump-to search is
 *  local, instant, and always the same set `visibleNav` already gates the sidebar/topbar with, so
 *  it can never surface (or hide) a page the actor's own nav doesn't. */
function flattenNav(items: readonly NavItem[], section?: string): PageHit[] {
  const hits: PageHit[] = [];
  for (const item of items) {
    hits.push({ to: item.to, label: item.label, section });
    if (item.children) hits.push(...flattenNav(item.children, item.label));
    if (item.subItems) {
      for (const sub of item.subItems) {
        hits.push({ to: `${item.to}/${sub.path}`, label: sub.label, section: item.label });
      }
    }
  }
  return hits;
}

/**
 * The topbar's "Search or jump to…" affordance — two independent result groups behind one box:
 * pages (local, instant, permission-filtered by the same `visibleNav` the nav itself uses — see
 * flattenNav above) and employees (debounced, server-side, via the `/search` contract). An
 * expanding popover rather than a route/modal of its own: opening it must cost nothing, since the
 * whole point of a ⌘K box is that it's faster than navigating.
 *
 * Its own local `selectedEmploymentId` state, independent of EmployeeDirectoryPage's — a result
 * clicked here opens the same `EmployeeDetailDialog` the directory list uses, but this component
 * has no directory list of its own to hold that state.
 */
export function GlobalSearch() {
  const { actor, me } = useAuthenticatedSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedEmploymentId, setSelectedEmploymentId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebouncedValue(query, 300);
  const trimmedQuery = debouncedQuery.trim();

  const switches = useNavSwitches();
  const travelOn = switches.travel;
  const pages = useMemo(() => {
    const flags = { travel: travelOn };
    return [
      ...flattenNav(visibleNav(actor, NAV, me.enabledModules, flags)),
      ...flattenNav(visibleNav(actor, ADMIN_NAV, me.enabledModules, flags)),
    ];
  }, [actor, me.enabledModules, travelOn]);
  // Local and free — no debounce needed for this half, but it's gated on the same debounced value
  // as the employee results so both groups update together rather than at two different rates.
  const pageHits =
    trimmedQuery.length < 1
      ? []
      : pages.filter((page) => {
          const haystack = `${page.section ?? ''} ${page.label}`.toLowerCase();
          return haystack.includes(trimmedQuery.toLowerCase());
        });

  function openSearch() {
    setOpen(true);
    // The input isn't mounted yet on the same tick the panel opens.
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function closeSearch() {
    setOpen(false);
    setQuery('');
  }

  // Global ⌘K (Ctrl+K on Windows/Linux) — opens and focuses search from anywhere in the shell,
  // matching the hint shown on the trigger itself.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openSearch();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Outside click / Escape closes the panel — same behaviour as packages/ui's DropdownMenu.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        closeSearch();
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') closeSearch();
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const searchQuery = tsr.search.search.useQuery({
    queryKey: ['search', 'global', trimmedQuery],
    queryData: { query: { q: trimmedQuery } },
    enabled: open && trimmedQuery.length >= 2,
  });

  const employeeQueryEnabled = open && trimmedQuery.length >= 2;
  const employeeHits =
    employeeQueryEnabled && searchQuery.data?.status === 200 ? searchQuery.data.body.employees : [];
  // Capped, same reasoning as the server-side employee cap — a jump-to list is for recognising the
  // page you want at a glance, not browsing every nav entry that happens to contain a letter.
  const visiblePageHits = pageHits.slice(0, 8);
  const isSearching = trimmedQuery.length >= 1;
  const isEmployeeSearchPending = employeeQueryEnabled && searchQuery.isFetching;

  return (
    <div ref={rootRef} className="relative hidden md:block">
      <button
        type="button"
        onClick={() => (open ? closeSearch() : openSearch())}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border border-border bg-bg-subtle px-3 py-2 text-xs text-fg-subtle transition-colors hover:text-fg-muted"
      >
        <Search className="size-3.5" />
        Search or jump to…
        <kbd className="rounded border border-border bg-surface px-1.5 py-px text-[10px] text-fg-muted">
          ⌘K
        </kbd>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Global search"
          className="absolute right-0 z-20 mt-2 w-80 rounded-md border border-border bg-surface-raised p-2 shadow-lg"
        >
          <Input
            ref={inputRef}
            label="Search"
            icon={<Search className="size-4" />}
            placeholder="Search employees, or jump to a page…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
          />

          <div className="mt-2 max-h-80 overflow-y-auto">
            {!isSearching ? (
              <p className="px-2 py-4 text-center text-xs text-fg-subtle">
                Start typing to search employees or jump to a page
              </p>
            ) : (
              <>
                {visiblePageHits.length > 0 && (
                  <div className="mb-1">
                    <p className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-fg-subtle">
                      Pages
                    </p>
                    <ul className="flex flex-col gap-0.5">
                      {visiblePageHits.map((page) => (
                        <li key={page.to}>
                          <button
                            type="button"
                            onClick={() => {
                              navigate(page.to);
                              closeSearch();
                            }}
                            className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-bg-subtle"
                          >
                            <Search className="size-3.5 shrink-0 text-fg-subtle" />
                            <span className="min-w-0 flex-1 truncate text-sm text-fg">
                              {page.section ? `${page.section} · ${page.label}` : page.label}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div>
                  <p className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-fg-subtle">
                    Employees
                  </p>
                  {trimmedQuery.length < 2 ? (
                    <p className="px-2 py-3 text-center text-xs text-fg-subtle">
                      Keep typing to search employees
                    </p>
                  ) : isEmployeeSearchPending ? (
                    <p className="px-2 py-3 text-center text-xs text-fg-subtle">Searching…</p>
                  ) : employeeHits.length === 0 ? (
                    <p className="px-2 py-3 text-center text-xs text-fg-subtle">
                      No employees found
                    </p>
                  ) : (
                    <ul className="flex flex-col gap-0.5">
                      {employeeHits.map((hit) => (
                        <li key={hit.employmentId}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedEmploymentId(hit.employmentId);
                              closeSearch();
                            }}
                            className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left hover:bg-bg-subtle"
                          >
                            <Avatar name={hit.displayName} imageUrl={hit.photoUrl} size="sm" />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-fg">
                                {hit.displayName}
                              </span>
                              {hit.designation && (
                                <span className="block truncate text-xs text-fg-muted">
                                  {hit.designation}
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {selectedEmploymentId && (
        <Suspense fallback={null}>
          <EmployeeDetailDialog
            employmentId={selectedEmploymentId}
            onClose={() => setSelectedEmploymentId(null)}
          />
        </Suspense>
      )}
    </div>
  );
}
